// src/lib/cancellationPolicy.js
//
// Patient-facing wording for a service version's cancellation policy.
//
// Input is the server's normalized policy (canonical keys, as read by the
// shared `read_cancellation_policy`):
//   { has_policy, window_hours (whole hours), fee_type: none|fixed|percentage,
//     fee_value, notes }
// At confirmation it comes from the held service VERSION; on an appointment it
// is the pinned snapshot. It is never taken from the live doctor profile.
//
// Honesty rules: nothing is charged or refunded automatically by the platform
// (fees are reviewed by the clinic), so the text never promises an automatic
// refund or charge. A policy with no window and no fee says exactly that.

import { formatDateTimeInZone } from "./displayTimezone";

function money(amount, currency) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  if (!currency) return value.toFixed(2);
  try {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

export function formatWindow(hours) {
  const value = Math.max(0, Math.floor(Number(hours) || 0));
  if (value && value % 24 === 0) {
    const days = value / 24;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  return `${value} hour${value === 1 ? "" : "s"}`;
}

function feeText(policy, currency) {
  const value = Number(policy.fee_value) || 0;
  if (policy.fee_type === "fixed" && value > 0) return `a cancellation fee of ${money(value, currency)}`;
  if (policy.fee_type === "percentage" && value > 0) {
    const pct = Number.isInteger(value) ? value : Number(value.toFixed(2));
    return `a cancellation fee of ${pct}% of the appointment price`;
  }
  return "";
}

// The instant the free-cancellation window closes, when there is one.
export function policyCutoffAt(policy, startAt) {
  const hours = Math.max(0, Math.floor(Number(policy?.window_hours) || 0));
  if (!policy?.has_policy || !hours || !startAt) return null;
  const start = new Date(startAt).getTime();
  return Number.isFinite(start) ? new Date(start - hours * 3600 * 1000) : null;
}

/**
 * Returns { hasPolicy, headline, details: string[] }.
 * `currency` is the appointment/service currency for fixed fees.
 * `startAt`/`timeZone` add the exact cutoff moment when both are known.
 */
export function describeCancellationPolicy(policy, { currency, startAt, timeZone } = {}) {
  if (!policy || !policy.has_policy) {
    return {
      hasPolicy: false,
      headline: "This service has no specific cancellation policy.",
      details: ["The standard appointment cancellation rules apply."],
    };
  }

  const fee = feeText(policy, currency);
  const hours = Math.max(0, Math.floor(Number(policy.window_hours) || 0));
  const details = [];
  let headline;

  if (!fee) {
    headline = "No cancellation fee.";
    if (hours) details.push(`Please give at least ${formatWindow(hours)} notice if you need to cancel.`);
  } else if (!hours) {
    headline = `No fee if you cancel before the appointment starts; otherwise ${fee} may apply.`;
  } else {
    headline = `Free cancellation up to ${formatWindow(hours)} before your appointment.`;
    details.push(`Cancelling later may incur ${fee}.`);
  }

  const cutoff = policyCutoffAt(policy, startAt);
  if (cutoff && fee && timeZone) {
    details.push(`Free cancellation until ${formatDateTimeInZone(cutoff.toISOString(), timeZone)} (${timeZone}).`);
  }
  if (fee) details.push("Any fee is reviewed by the clinic; nothing is charged or refunded automatically.");
  if (policy.notes) details.push(policy.notes);
  return { hasPolicy: true, headline, details };
}
